<script setup lang="ts">
import { Plus, EditPencil, Trash } from "@iconoir/vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import type { Workspace } from "@artech-global/types";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import WorkspaceFormModal from "./WorkspaceFormModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "Workspace" });

const { t } = useI18n();
const { d } = useFormat();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const defaultFilter = {
  keyword: "",
  status: "all",
  page: 1,
  pageSize: 20,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<Workspace, typeof defaultFilter>({
  endpoint: "/workspaces",
  defaultFilter,
});

const { del, errorMsg } = useCrud<Workspace>({
  endpoint: "/workspaces",
});

const formRef = ref<InstanceType<typeof WorkspaceFormModal> | null>(null);

const statusOptions = computed(() => [
  { value: "all", label: t("workspace.statusAll") },
  { value: "deleted", label: t("workspace.statusDeleted") },
]);

function openCreate() {
  formRef.value?.open();
}

function openEdit(row: Workspace) {
  formRef.value?.open(row.id as string);
}

async function confirmDelete(row: Workspace) {
  const ok = await alert.negative({
    title: t("workspace.deleteConfirmTitle"),
    description: t("workspace.deleteConfirmBody", { name: row.name }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id as any);
    toast.success({ title: t("message.deleteSuccess") });
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(1, Math.ceil(nextItemCount / filter.pageSize));
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

async function onSaved() {
  await load();
}

onMounted(async () => {
  setTitle(t("workspace.title"));
  await load();
});

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => filter.status,
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :title="t('workspace.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('workspace.itemsSuffix')}` }}
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.WorkspacesWrite)"
        variant="primary"
        size="small"
        @click="openCreate"
      >
        <Plus class="icon" />
        {{ t("workspace.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty
        :title="t('workspace.emptyTitle')"
        :subtitle="t('workspace.emptySubtitle')"
      >
        <template #action>
          <Button v-if="can(AdminPermission.WorkspacesWrite)" variant="primary" size="small" @click="openCreate">
            <Plus class="icon" />
            {{ t("workspace.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('workspace.emptySearchTitle')"
        :subtitle="t('workspace.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'workspace', label: t('workspace.columnName'), class: 'md:min-w-[240px]' },
          { key: 'desc', label: t('workspace.columnDesc'), class: 'min-w-[160px] hidden md:table-cell' },
          { key: 'createTime', label: t('workspace.columnCreatedAt'), class: 'min-w-[160px] hidden md:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[80px]' },
        ]"
        :items="items"
        @row-click="(row) => navigateTo(`/workspace/${row.id}`)"
      >
        <template #cell-workspace="{ row }">
          <div class="flex items-center gap-small">
            <Avatar
              :src="row.image"
              :initials="(row.name || '').substring(0, 2).toUpperCase()"
              size="small"
              shape="square"
            />
            <span class="font-semibold">{{ row.name }}</span>
          </div>
        </template>

        <template #cell-desc="{ row }">
          <span v-if="row.desc">{{ row.desc }}</span>
          <span v-else class="content-neutral-medium">—</span>
        </template>

        <template #cell-createTime="{ row }">
          {{ d(row.createTime, 'datetime') }}
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.WorkspacesWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              @click.stop="openEdit(row)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>

            <ButtonIcon
              v-if="can(AdminPermission.WorkspacesDelete)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              @click.stop="confirmDelete(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <WorkspaceFormModal ref="formRef" @saved="onSaved" />
</template>
